import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';

const CATEGORY_META = {
  onboarding:  { icon: '🤝', label: 'Onboarding',       color: '#3b7ef0' },
  approval:    { icon: '✅', label: 'Content Approval',  color: '#e87830' },
  report:      { icon: '📊', label: 'Report Delivery',   color: '#9050e8' },
  checkin:     { icon: '💬', label: 'Check-in / Update', color: '#1db87a' },
  feedback:    { icon: '⭐', label: 'Feedback Request',  color: '#f7c34f' },
  escalation:  { icon: '🚨', label: 'Escalation',        color: '#e84545' },
  invoice:     { icon: '💰', label: 'Invoice / Payment', color: '#18b8b8' },
  general:     { icon: '📝', label: 'General',           color: '#888'    },
};

export default function Touchpoints() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [messageBody, setMessageBody] = useState('');
  const [filterCat, setFilterCat] = useState('All');
  const [searchTpl, setSearchTpl] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [clientRes, tplRes] = await Promise.all([
        api.get('/clients'),
        api.get('/message-templates'),
      ]);
      setClients(clientRes.data?.data || clientRes.data || []);
      const tpls = (tplRes.data?.data || tplRes.data || []).filter(t => t.enabled !== false);
      setTemplates(tpls);
    } catch (e) {
      console.error('Failed to load data', e);
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setMessageBody(tpl.body || tpl.content || '');
  };

  const handleTemplateDropdown = (e) => {
    const id = e.target.value;
    const tpl = templates.find(t => String(t.id) === String(id));
    if (tpl) selectTemplate(tpl);
  };

  const [sending, setSending] = useState(false);

  const sendEmail = async () => {
    if (!selectedClient || !messageBody.trim()) {
      alert('Please select a client and enter a message.');
      return;
    }
    const client = clients.find(c => String(c.id) === String(selectedClient));
    if (!client?.email) {
      alert('This client does not have an email address.');
      return;
    }
    setSending(true);
    try {
      await api.post('/touchpoint-messages/send-email', {
        to_email: client.email,
        to_name: client.name,
        subject: `${selectedTemplate ? selectedTemplate.name : 'Message'} - ${client.name}`,
        body: messageBody,
        client_id: client.id,
        touchpoint_type: selectedTemplate ? selectedTemplate.name : 'general',
      });
      alert('Email sent successfully!');
      setMessageBody('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to send email. Check SMTP settings in Settings → Communication.');
    }
    setSending(false);
  };

  const sendWhatsApp = () => {
    if (!selectedClient || !messageBody.trim()) {
      alert('Please select a client and enter a message.');
      return;
    }
    const text = encodeURIComponent(messageBody);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    logMessage('WhatsApp');
  };

  const copyMessage = () => {
    if (!messageBody.trim()) {
      alert('Please enter a message first.');
      return;
    }
    navigator.clipboard.writeText(messageBody).then(() => {
      alert('Message copied to clipboard!');
    });
    logMessage('Clipboard');
  };

  const logMessage = (channel) => {
    const client = clients.find(c => String(c.id) === String(selectedClient));
    const newMsg = {
      id: Date.now(),
      client_name: client ? client.name : 'Unknown',
      touchpoint_type: selectedTemplate ? selectedTemplate.name : 'Custom',
      channel,
      body: messageBody,
      sent_by: user?.name || 'You',
      sent_at: new Date().toISOString(),
    };
    setMessages(prev => [newMsg, ...prev]);
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ', ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  // Category counts and filtering
  const catCounts = {};
  templates.forEach(t => {
    const cat = t.category || 'general';
    catCounts[cat] = (catCounts[cat] || 0) + 1;
  });
  const allCats = ['All', ...Object.keys(CATEGORY_META)];

  const filteredTemplates = templates.filter(t => {
    const matchCat = filterCat === 'All' || t.category === filterCat;
    const matchSearch = !searchTpl || t.name?.toLowerCase().includes(searchTpl.toLowerCase());
    return matchCat && matchSearch;
  });

  if (loading) return (
    <div className="page-wrap">
      <div className="empty-state">
        <div className="empty-state-icon">⏳</div>
        <div className="empty-state-title">Loading...</div>
      </div>
    </div>
  );

  return (
    <div className="page-wrap">
      {/* Info Alert */}
      <div style={{
        background: 'rgba(59,126,240,0.08)',
        border: '1px solid rgba(59,126,240,0.2)',
        borderRadius: 'var(--radius-sm)',
        padding: '12px 16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '0.82rem',
        color: 'var(--accent)',
      }}>
        <span style={{ fontSize: '1rem' }}>ℹ️</span>
        <span>Customer Touchpoints are direct client interaction moments. Use the templates below to send messages, share previews, and capture feedback for each touchpoint in the workflow.</span>
      </div>

      {/* Two-column layout: Template Actions + Message Log */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '16px', marginBottom: '16px' }}>

        {/* Left: Touchpoint Templates */}
        <div>
          <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🎯</span> Touchpoint Actions
          </div>

          {/* Category filter pills */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
            {allCats.map(cat => {
              const info = cat === 'All'
                ? { icon: '📋', label: 'All', color: 'var(--accent)' }
                : (CATEGORY_META[cat] || { icon: '📝', label: cat, color: '#888' });
              const count = cat === 'All' ? templates.length : (catCounts[cat] || 0);
              if (cat !== 'All' && count === 0) return null;
              const isActive = filterCat === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setFilterCat(cat)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '5px 12px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 600,
                    cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                    background: isActive ? info.color : 'var(--bg3)',
                    color: isActive ? '#fff' : 'var(--text2)',
                    transition: 'all 0.15s',
                  }}
                >
                  {info.icon} {info.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Template cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredTemplates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text3)', fontSize: '0.82rem' }}>
                {templates.length === 0
                  ? <>No message templates found. Go to <b>Master Data → Templates</b> and click <b>⚡ Add Standard Templates</b> to get started.</>
                  : 'No templates match your filter.'}
              </div>
            ) : (
              filteredTemplates.map(tpl => {
                const catMeta = CATEGORY_META[tpl.category] || CATEGORY_META.general;
                const isSelected = selectedTemplate && selectedTemplate.id === tpl.id;
                return (
                  <div
                    key={tpl.id}
                    onClick={() => selectTemplate(tpl)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      background: isSelected ? 'rgba(59,126,240,0.06)' : 'var(--bg3)',
                      border: isSelected ? '1px solid var(--accent)' : '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ fontSize: '1.2rem' }}>{tpl.icon || catMeta.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>{tpl.name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(tpl.body || tpl.content || '').substring(0, 60)}...
                      </div>
                    </div>
                    <span
                      className="badge"
                      style={{
                        background: catMeta.color + '18',
                        color: catMeta.color,
                        border: '1px solid ' + catMeta.color + '40',
                        flexShrink: 0,
                      }}
                    >
                      {catMeta.label}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right: Message Log */}
        <div className="card" style={{ alignSelf: 'start' }}>
          <div className="card-title" style={{ marginBottom: '12px' }}>
            <span>📁</span> Message Log
          </div>
          {messages.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: '0.82rem', fontStyle: 'italic' }}>No messages sent yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '400px', overflowY: 'auto' }}>
              {messages.map(m => (
                <div key={m.id} style={{ padding: '10px 12px', background: 'var(--bg3)', borderRadius: 'var(--radius-xs)', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span className="badge badge-blue" style={{ fontSize: '0.68rem' }}>{m.touchpoint_type}</span>
                    <span style={{ fontSize: '0.68rem', color: 'var(--text3)' }}>{m.channel}</span>
                  </div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--text2)', marginBottom: '4px' }}>{m.client_name}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text3)' }}>{formatDate(m.sent_at)} · by {m.sent_by}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Compose Message */}
      <div className="card">
        <div className="card-title" style={{ marginBottom: '16px' }}>
          <span>✍️</span> Compose Message
        </div>

        <div className="form-row" style={{ marginBottom: '14px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Client / Recipient</label>
            <select
              className="form-select"
              value={selectedClient}
              onChange={e => setSelectedClient(e.target.value)}
            >
              <option value="">Select a client...</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.contact_person ? ` — ${c.contact_person}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Touchpoint Type</label>
            <select
              className="form-select"
              value={selectedTemplate ? selectedTemplate.id : ''}
              onChange={handleTemplateDropdown}
            >
              <option value="">Select a template...</option>
              {templates.map(tpl => (
                <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Message</label>
          <textarea
            className="form-textarea"
            rows={5}
            value={messageBody}
            onChange={e => setMessageBody(e.target.value)}
            placeholder="Select a template above or type your message here..."
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
          <button className="btn btn-primary" onClick={sendEmail} disabled={sending}>
            📧 Send via Email
          </button>
          <button className="btn btn-secondary" onClick={sendWhatsApp}>
            💬 Send via WhatsApp
          </button>
          <button className="btn btn-secondary" onClick={copyMessage}>
            📋 Copy to Clipboard
          </button>
        </div>
      </div>
    </div>
  );
}
